import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { videosAPI } from '../services/api';

/**
 * Hook to fetch and cache video thumbnails using React Query.
 * Thumbnails are cached globally and persist across navigation.
 *
 * @param {string[]} videoIds - Array of video IDs to fetch thumbnails for
 * @returns {Object} thumbnailsMap - Map of videoId -> thumbnailUrl
 */
export function useThumbnails(videoIds = []) {
  const queryClient = useQueryClient();

  // Filter to only IDs we don't already have cached
  const uncachedIds = videoIds.filter(id => {
    if (!id) return false;
    const cached = queryClient.getQueryData(['thumbnail', id]);
    return !cached;
  });

  // Batch fetch uncached thumbnails
  const { data: batchData } = useQuery({
    queryKey: ['thumbnails-batch', uncachedIds.sort().join(',')],
    queryFn: async () => {
      if (uncachedIds.length === 0) return { thumbnails: {} };
      const response = await videosAPI.getThumbnails(uncachedIds);
      return response?.data || { thumbnails: {} };
    },
    enabled: uncachedIds.length > 0,
    staleTime: 30 * 60 * 1000, // 30 minutes - thumbnails rarely change
    gcTime: 60 * 60 * 1000, // Keep in cache for 1 hour
  });

  // When batch data arrives, cache individual thumbnails
  useEffect(() => {
    if (batchData?.thumbnails) {
      Object.entries(batchData.thumbnails).forEach(([videoId, url]) => {
        queryClient.setQueryData(['thumbnail', videoId], url);
      });
    }
  }, [batchData, queryClient]);

  // Build thumbnails map from individual caches
  const thumbnailsMap = {};
  videoIds.forEach(id => {
    if (!id) return;
    // First check individual cache
    const cached = queryClient.getQueryData(['thumbnail', id]);
    if (cached) {
      thumbnailsMap[id] = cached;
    } else if (batchData?.thumbnails?.[id]) {
      // Fallback to batch data if not yet in individual cache
      thumbnailsMap[id] = batchData.thumbnails[id];
    }
  });

  return thumbnailsMap;
}
